import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { ReactRenderer } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import tippy from 'tippy.js';
import { ParticipantTaskReference } from '../../../../hooks/useParticipantTaskReferenceSearch';

const getTaskReference = (task: ParticipantTaskReference) =>
    `${task.organizationAbbreviation}-${task.friendlyId}`;

const TaskListPopup = forwardRef((props: any, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    useEffect(() => setSelectedIndex(0), [props.items]);

    const selectItem = (index: number) => {
        const task = props.items[index] as ParticipantTaskReference | undefined;
        if (!task) return;
        const reference = getTaskReference(task);
        props.command({ id: reference, label: reference });
    };

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === 'ArrowUp') {
                setSelectedIndex(previous => (previous + props.items.length - 1) % props.items.length);
                return true;
            }
            if (event.key === 'ArrowDown') {
                setSelectedIndex(previous => (previous + 1) % props.items.length);
                return true;
            }
            if ((event.key === 'Enter' || event.key === 'Tab') && props.items.length) {
                selectItem(selectedIndex);
                return true;
            }
            return false;
        },
    }));

    return (
        <div className="chat-mention-suggestion flex w-72 flex-col overflow-hidden rounded-xl bg-white p-1 shadow-xl dark:bg-slate-900">
            {props.items.length ? props.items.map((task: ParticipantTaskReference, index: number) => {
                const reference = getTaskReference(task);
                return (
                    <button
                        type="button"
                        key={task.id}
                        onClick={() => selectItem(index)}
                        className={`chat-task-suggestion-option flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${index === selectedIndex ? 'chat-task-suggestion-option--active bg-slate-100 dark:bg-white/[0.06]' : 'hover:bg-slate-50 dark:hover:bg-white/[0.04]'}`}
                    >
                        <span className="chat-task-suggestion-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-200 font-mono text-base font-bold leading-none text-slate-600 dark:bg-white/[0.08] dark:text-slate-300">#</span>
                        <span className="min-w-0 flex-1">
                            <span className="chat-task-suggestion-id block truncate font-mono text-[11px] font-semibold text-slate-600 dark:text-slate-300">{reference}</span>
                            <span className="block truncate text-[13px] text-slate-700 dark:text-slate-200">{task.title}</span>
                            {task.participantName && <span className="block truncate text-[11px] text-slate-400">{task.participantName}</span>}
                        </span>
                    </button>
                );
            }) : (
                <div className="px-3 py-3 text-center text-sm italic text-slate-400">No matching participant tasks</div>
            )}
        </div>
    );
});

TaskListPopup.displayName = 'TaskListPopup';

export default function getTaskSuggestions(searchTasks: (query: string) => Promise<ParticipantTaskReference[]>) {
    return {
        char: '#',
        pluginKey: new PluginKey('participantTaskReferenceSuggestion'),
        items: async ({ query }: { query: string }) => searchTasks(query),
        render: () => {
            let component: ReactRenderer<any>;
            let popup: any[];
            return {
                onStart: (props: any) => {
                    component = new ReactRenderer(TaskListPopup, { props, editor: props.editor });
                    if (!props.clientRect) return;
                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'top-start',
                    });
                },
                onUpdate: (props: any) => {
                    component.updateProps(props);
                    if (props.clientRect) popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
                },
                onKeyDown: (props: any) => {
                    if (props.event.key === 'Escape') {
                        popup?.[0]?.hide();
                        return true;
                    }
                    return (component.ref as any)?.onKeyDown(props);
                },
                onExit: () => {
                    popup?.[0]?.destroy();
                    component?.destroy();
                },
            };
        },
    };
}
